/**
 * =====================================================
 * GLOBAL PRODUCT STOCK CONFIGURATION
 * =====================================================
 * 
 * This file contains the stock status settings for all products.
 * Update this file to control product availability across the site.
 * 
 * HOW TO USE:
 * -----------
 * 1. Find the product you want to update
 * 2. Change the 'status' field to one of:
 *    - "IN_STOCK"   → Product is available for purchase
 *    - "SOLD_OUT"   → Product is not available (Add to Cart disabled)
 * 
 * 3. Save the file and refresh the website
 * 
 * BEHAVIOR:
 * ---------
 * - IN_STOCK: 
 *   • Add to Cart button is ENABLED
 *   • Shows delivery time: "1-2 days"
 *   • Stock status shows "In Stock" (green)
 * 
 * - SOLD_OUT:
 *   • Add to Cart button is DISABLED
 *   • Shows "Sold Out" message
 *   • Stock status shows "Sold Out" (red)
 * 
 * =====================================================
 */

// Stock status constants
export const STOCK_STATUS = {
  IN_STOCK: 'IN_STOCK',
  SOLD_OUT: 'SOLD_OUT'
};

// Delivery time for in-stock products
export const DELIVERY_TIME = '1-2 days';

/**
 * UNITS PER PACK CONFIGURATION
 * ============================
 * Defines how many units are in one pack for each cutlery product
 */
export const UNITS_PER_PACK = {
  'edible-spoons': 10,
  'edible-forks': 10,
  'edible-ice-cream-spoon': 10,
  'edible-coffee-stirrer': 30,
  'edible-coffee-stirrer-chocolate': 30,
  'edible-coffee-stirrer-vanilla': 30,
  'french-fries-fork': 30
};

/**
 * Get units per pack for a product
 * @param {string} productId - The product ID
 * @returns {number} - Units per pack (default 1 if not found)
 */
export const getUnitsPerPack = (productId) => {
  // Check for exact match first
  if (UNITS_PER_PACK[productId]) {
    return UNITS_PER_PACK[productId];
  }
  // Check for partial match (e.g., 'edible-coffee-stirrer-chocolate' matches 'edible-coffee-stirrer')
  for (const key of Object.keys(UNITS_PER_PACK)) {
    if (productId.startsWith(key)) {
      return UNITS_PER_PACK[key];
    }
  }
  return 1;
};

/**
 * Check if a product is a cutlery product (has units per pack)
 * @param {string} productId - The product ID
 * @returns {boolean}
 */
export const isCutleryProduct = (productId) => {
  return Object.keys(UNITS_PER_PACK).some(key => productId.startsWith(key));
};

/**
 * Calculate total units for a cutlery product
 * @param {string} productId - The product ID
 * @param {number} quantity - Number of packs
 * @returns {number} - Total units
 */
export const calculateTotalUnits = (productId, quantity) => {
  return getUnitsPerPack(productId) * quantity;
};

/**
 * PRODUCT STOCK CONFIGURATION
 * ===========================
 * Update the status for each product below
 */
export const productStock = {
  // Edible Cups
  'edible-cup-110': {
    name: 'Edible CUP | 110 ml',
    status: STOCK_STATUS.SOLD_OUT,
    deliveryTime: DELIVERY_TIME
  },
  'edible-cup-220': {
    name: 'Edible CUP | 220 ml',
    status: STOCK_STATUS.IN_STOCK,
    deliveryTime: DELIVERY_TIME
  },

  // Cutlery & Tableware
  'edible-spoons': {
    name: 'Edible Spoons',
    status: STOCK_STATUS.SOLD_OUT,
    deliveryTime: DELIVERY_TIME
  },
  'edible-forks': {
    name: 'Edible Forks',
    status: STOCK_STATUS.IN_STOCK,
    deliveryTime: DELIVERY_TIME
  },
  'edible-coffee-stirrer': {
    name: 'Edible Coffee Stirrer',
    status: STOCK_STATUS.IN_STOCK,
    deliveryTime: DELIVERY_TIME
  },
  'edible-coffee-stirrer-chocolate': {
    name: 'Edible Coffee Stirrer - Chocolate',
    status: STOCK_STATUS.IN_STOCK,
    deliveryTime: DELIVERY_TIME
  },
  'edible-coffee-stirrer-vanilla': {
    name: 'Edible Coffee Stirrer - Vanilla',
    status: STOCK_STATUS.IN_STOCK,
    deliveryTime: DELIVERY_TIME
  },
  'edible-ice-cream-spoon': {
    name: 'Edible Ice Cream Spoon',
    status: STOCK_STATUS.SOLD_OUT,
    deliveryTime: DELIVERY_TIME
  },
  'french-fries-fork': {
    name: 'French Fries Fork',
    status: STOCK_STATUS.IN_STOCK,
    deliveryTime: DELIVERY_TIME
  }
};

/**
 * Get stock information for a product
 * @param {string} productId - The product ID
 * @returns {object} - { status, isSoldOut, isInStock, deliveryTime, statusLabel }
 */
export const getProductStock = (productId) => {
  const product = productStock[productId];
  
  if (!product) {
    console.warn(`Product "${productId}" not found in productConfig.js`);
    return {
      status: STOCK_STATUS.IN_STOCK,
      isSoldOut: false,
      isInStock: true,
      deliveryTime: DELIVERY_TIME,
      statusLabel: 'In Stock'
    };
  }
  
  const isSoldOut = product.status === STOCK_STATUS.SOLD_OUT;
  const isInStock = product.status === STOCK_STATUS.IN_STOCK;
  
  return {
    status: product.status,
    isSoldOut,
    isInStock,
    deliveryTime: isInStock ? product.deliveryTime : null,
    statusLabel: isSoldOut ? 'Sold Out' : 'In Stock'
  };
};

/**
 * Check if a product is sold out
 * @param {string} productId - The product ID
 * @returns {boolean} - true if sold out, false otherwise
 */
export const isProductSoldOut = (productId) => {
  const stock = getProductStock(productId);
  return stock.isSoldOut;
};

/**
 * Check if a product is in stock
 * @param {string} productId - The product ID
 * @returns {boolean} - true if in stock, false otherwise
 */
export const isProductInStock = (productId) => {
  const stock = getProductStock(productId);
  return stock.isInStock;
};

/**
 * Get delivery time for a product
 * @param {string} productId - The product ID
 * @returns {string|null} - Delivery time string or null if sold out
 */
export const getDeliveryTime = (productId) => {
  const stock = getProductStock(productId);
  return stock.deliveryTime;
};

/**
 * Get display-friendly stock status label
 * @param {string} productId - The product ID
 * @returns {string} - "In Stock" or "Sold Out"
 */
export const getStockLabel = (productId) => {
  const stock = getProductStock(productId);
  return stock.statusLabel;
};

/**
 * Update product stock status (for programmatic updates)
 * Note: This only updates the in-memory state. For persistent changes,
 * update the productStock object above.
 * @param {string} productId - The product ID
 * @param {string} newStatus - STOCK_STATUS.IN_STOCK or STOCK_STATUS.SOLD_OUT
 */
export const updateProductStock = (productId, newStatus) => {
  if (productStock[productId]) {
    productStock[productId].status = newStatus;
  } else {
    console.warn(`Cannot update stock: Product "${productId}" not found`);
  }
};

export default {
  STOCK_STATUS,
  DELIVERY_TIME,
  UNITS_PER_PACK,
  productStock,
  getProductStock,
  isProductSoldOut,
  isProductInStock,
  getDeliveryTime,
  getStockLabel,
  updateProductStock,
  getUnitsPerPack,
  isCutleryProduct,
  calculateTotalUnits
};
